// src/layouts/FormLayout.js
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { safeGoBack } from "../utils/wkCommon";
import { getLang, setLang, LANG_ID, LANG_ZH } from "../utils/language";

import { useLocation } from "react-router-dom";
import { logVisit } from "../utils/wkCommon";

/**
 * FormLayout (mobile collapsible panels)
 *
 * Props:
 * - title / titleNode
 * - onSubmit (fn)
 * - submitLabel
 * - leftPanelNode (jsx)    -> full summary/detail (desktop)
 * - leftSummaryNode (jsx)  -> one-line summary shown on mobile collapsed view (optional)
 * - rightPanelNode (jsx)   -> full instructions (desktop)
 * - rightSummaryTitle (str)-> title for instructions on mobile (default: "Petunjuk Pengisian")
 * - infoNode (jsx)         -> baris informasi / validation messages
 * - children               -> form fields (no <form> wrapper here)
 */
export default function FormLayout({
  title = "Form",
  titleNode,
  onSubmit,
  submitLabel = "SUBMIT",
  leftPanelNode = null,
  leftSummaryNode = null,
  rightPanelNode = null,
  rightSummaryTitle = "Petunjuk Pengisian",
  infoNode = null,
  hideTopButton = false,
  hideBottomButton = false,
  hideBackButton = false,
  children,
  hideLangSwitch = false,
  headerActions = null
}) {
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 900 : true);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const history = useHistory();

  // mobile-specific collapsed states (default collapsed)
  const [leftMobileOpen, setLeftMobileOpen] = useState(false);
  const [rightMobileOpen, setRightMobileOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    logVisit({
      path: location.pathname,
      query: location.search,
    });
  }, [location.pathname]);
  
  const [lang, setLangState] = React.useState(getLang());

  function toggleLang() {
    const newLang = lang === LANG_ZH ? LANG_ID : LANG_ZH;
    setLang(newLang);
    window.location.reload();
  }

  useEffect(() => {
    function onResize() {
      const d = window.innerWidth >= 900;
      setIsDesktop(d);
      if (d) {
        setLeftOpen(!!leftPanelNode);
        setRightOpen(!!rightPanelNode);
      } else {
        // hide panels by default on mobile (we show them above form as collapsibles)
        setLeftOpen(false);
        setRightOpen(false);
      }
    }
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [leftPanelNode, rightPanelNode]);

  // determine grid for desktop (mobile will use single column)
  const showLeft = Boolean(leftPanelNode) && isDesktop;
  const showRight = Boolean(rightPanelNode) && isDesktop;
  let gridTemplate = "1fr";
  if (showLeft && showRight) gridTemplate = "300px 1fr 320px";
  else if (showLeft) gridTemplate = "300px 1fr";
  else if (showRight) gridTemplate = "1fr 320px";

  function handleSubmit(e) {
    if (typeof onSubmit === "function") {
      e.preventDefault();
      onSubmit(e);
    }
  }

  const headerTitle = titleNode ? titleNode : <span>{title}</span>;

  return (
    <div className="form-layout">
      <header className="form-header">
        <div className="header-inner">
          <div className="header-left">
            {!hideBackButton && (
            <button className="btn-icon back-btn" onClick={() => safeGoBack(history)} aria-label="Back">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clipRule="evenodd" />
              </svg>
            </button>
            )}
            <div className="page-title">{headerTitle}</div>
          </div>
          <div className="header-right" >
            {!hideLangSwitch && (
              <button className="btn-icon" onClick={toggleLang} title="切換語言 Ubah Bahasa">
                {lang === LANG_ZH ? "ID" : "文"}
              </button>
            )}

            {headerActions && (
              <div style={{ display: "flex", gap: "0.4rem" }}>
                {headerActions}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* info / validation row */}
      <div className="form-info-row" role="note">
        {infoNode}
      </div>

      {/* MOBILE: collapsed panels shown above form (only when not desktop) */}
      {!isDesktop && (
        <div className="mobile-panels">
          {rightPanelNode && (
            <div className="mobile-panel">
              <button
                type="button"
                className="mobile-panel-toggle"
                aria-expanded={rightMobileOpen}
                onClick={() => setRightMobileOpen((s) => !s)}
              >
                <div className="mobile-panel-title"><strong>{rightSummaryTitle}</strong></div>
                <div className="mobile-panel-chev">{rightMobileOpen ? "▾" : "▸"}</div>
              </button>

              <div className={`mobile-panel-body ${rightMobileOpen ? "open" : "collapsed"}`}>
                <div className="panel-inner">{rightPanelNode}</div>
              </div>
            </div>
          )}

          {leftPanelNode && (
            <div className="mobile-panel">
              <button
                type="button"
                className="mobile-panel-toggle"
                aria-expanded={leftMobileOpen}
                onClick={() => setLeftMobileOpen((s) => !s)}
              >
                <div className="mobile-panel-title">
                  {leftSummaryNode ? leftSummaryNode : <strong>Summary</strong>}
                </div>
                <div className="mobile-panel-chev">{leftMobileOpen ? "▾" : "▸"}</div>
              </button>

              <div className={`mobile-panel-body ${leftMobileOpen ? "open" : "collapsed"}`}>
                <div className="panel-inner">{leftPanelNode}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MAIN GRID */}
      <div className="form-body" style={{ gridTemplateColumns: isDesktop ? gridTemplate : "1fr" }}>
        {/* LEFT (desktop summary) */}
        {showLeft && (
          <aside className={`left-panel desktop ${leftOpen ? "open" : "closed"}`}>
            <div className="panel-inner">{leftPanelNode}</div>
          </aside>
        )}

        {/* MAIN FORM */}
        <main className="form-main">
          <form className="form-wrapper" onSubmit={handleSubmit}>
            <div className="form-top-actions">
              <div className="form-top-left" />
              <div className="form-top-right">
                {!hideTopButton && (
                  <button type="submit" className="btn-submit">{submitLabel}</button>
                )}
              </div>
            </div>

            <div className="form-content">{children}</div>

            {!hideBottomButton && (
            <div className="form-bottom-actions">
              <hr className="divider" />
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 18px" }}>
                <button type="submit" className="btn-submit">{submitLabel}</button>
              </div>
            </div>
            )}
          </form>
        </main>

        {/* RIGHT (desktop instructions) */}
        {showRight && (
          <aside className={`right-panel desktop ${rightOpen ? "open" : "closed"}`}>
            <div className="panel-inner">{rightPanelNode}</div>
          </aside>
        )}
      </div>

      <style>{`
        .form-layout { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; }
        .form-header { background: #244f8f; color: white; padding: 14px 18px; box-shadow: 0 1px 0 rgba(0,0,0,0.06); }
        .header-inner { display:flex; justify-content:space-between; align-items:center; }
        .header-left { display:flex; align-items:center; gap:12px; }
        .page-title { font-weight:700; font-size:18px; }
        .btn-icon { background: transparent; color: white; border: 1px solid rgba(255,255,255,0.18); padding:6px 8px; border-radius:8px; cursor:pointer; }
        .form-info-row { padding: 10px 18px; background: #fff; border-bottom: 1px solid #f0f0f0; }

        /* mobile collapsible panels (above form) */
        .mobile-panels { padding: 12px 18px; display:flex; flex-direction:column; gap:10px; }
        .mobile-panel { background: transparent; }
        .mobile-panel-toggle {
          display:flex;
          justify-content:space-between;
          align-items:center;
          width:100%;
          padding:10px 12px;
          border-radius:8px;
          border:1px solid #e6eaf1;
          background:#fff;
          cursor:pointer;
        }
        .mobile-panel-title { text-align:left; font-weight:600; color:#2b4a84; }
        .mobile-panel-chev { font-size:14px; opacity:0.8; }

        .mobile-panel-body { overflow: hidden; max-height: 0; transition: max-height 220ms ease; }
        .mobile-panel-body.open { max-height: 400px; padding-top: 8px; }
        .mobile-panel-body .panel-inner { background: #fff; border-radius: 8px; padding: 10px; box-shadow: 0 6px 18px rgba(10,10,10,0.04); }

        .form-body { display:grid; gap: 18px; padding: 18px; }

        .left-panel, .right-panel {
          background: #fff;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 4px 12px rgba(10,10,10,0.04);
        }
        .panel-inner { max-height: 72vh; overflow:auto; }

        .form-main { min-height: 220px; }

        .form-wrapper {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 6px 18px rgba(10,10,10,0.04);
          width: 100%;
          overflow: visible; /* avoid clipping children */
        }

        .form-top-actions { display:flex; justify-content: space-between; align-items:center; padding: 12px 18px; border-bottom: 1px solid #e9eef6; background: #fff; }
        .btn-submit { background: #2b4a84; color: white; border: none; padding: 8px 18px; border-radius: 8px; font-weight: 700; cursor: pointer; }

        .form-content { padding: 18px; }

        .form-bottom-actions .divider { border: none; height: 1px; background: #e9eef6; margin: 0; }

        /* ensure controls box-sizing and width behavior */
        input, textarea, select, button { box-sizing: border-box; }
        input[type="text"], input[type="email"], input[type="date"], select, textarea { width: 100%; }

        @media (max-width: 900px) {
          .form-body { grid-template-columns: 1fr; padding: 12px; }
          .left-panel, .right-panel {  } /* desktop panels hidden on mobile (we use mobile-panels) */
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
      `}</style>
    </div>
  );
}
